import { z } from "zod";
import { DisplayName, MeResponse, UserId } from "./user.ts";

/**
 * Перенос заглушки по ссылке (`docs/account-adopt-design.md`, раздел 8).
 *
 * Ученик, который уже вошёл по ссылке одного преподавателя, открывает
 * постоянную ссылку второго. Вместо тупика «Вы уже вошли» сервер говорит,
 * можно ли спросить «это один ученик?», и «Да» шлёт подтверждение.
 */

/**
 * Что предложить на экране 409 (8.1). Отсутствие поля значит «не предлагать»:
 * старый экран «Вы уже вошли».
 *
 * ⚠️ Причин отказа здесь нет намеренно: «нельзя» одно на все причины, а «на
 * занятии» приходит только ответом на «Да». `currentTeachers` только при
 * предложении: список преподавателей вошедшего виден тому, кто держит ключ
 * второго, и показывается ровно тогда, когда без него на вопрос не ответить.
 */
export const AdoptOffer = z.discriminatedUnion("verdict", [
  z.object({
    verdict: z.literal("offer"),
    /** Своё имя сессии (10.1, пункт 3). */
    currentDisplayName: DisplayName,
    /** Преподаватели видимых сессии живых пар, по алфавиту. */
    currentTeachers: z.array(DisplayName),
  }),
  z.object({
    verdict: z.literal("cannot"),
    currentDisplayName: DisplayName,
    /**
     * «Нельзя из этого браузера» (строка 9б): он вошёл ключом не всех пар
     * ученика. Экран ведёт ко входу паролем или к ссылкам остальных
     * преподавателей здесь же. Только этот признак, без прочих причин.
     */
    openElsewhere: z.literal(true).optional(),
  }),
]);
export type AdoptOffer = z.infer<typeof AdoptOffer>;

/**
 * Тело `409 already_signed_in` погашения ссылки. Переехало сюда из
 * `apps/web/src/lib/api.ts`: две копии формы однажды разойдутся.
 *
 * Новые поля необязательны: старый API их не пришлёт, а старые вкладки лишних
 * не заметят (`z.object` их отбрасывает).
 */
export const AlreadySignedIn = z.object({
  error: z.literal("already_signed_in"),
  currentUserId: z.string(),
  /** Имя на паре ссылки. */
  studentDisplayName: z.string(),
  teacherDisplayName: z.string(),
  /** Ученик ссылки: ключ памяти ответа «Нет» на устройстве (9.4). */
  linkStudentUserId: z.string().optional(),
  adopt: AdoptOffer.optional(),
});
export type AlreadySignedIn = z.infer<typeof AlreadySignedIn>;

/**
 * Подтверждение: `POST /v1/invites/:code/adopt`.
 *
 * `currentUserId` это владелец сессии, которого видел экран: сменился, и
 * сервер ответит `409 adopt_stale`, а не перенесёт не того. `confirm: true`
 * обязателен, чтобы пустой запрос не был согласием.
 */
export const AdoptRequest = z.object({
  currentUserId: UserId,
  confirm: z.literal(true),
});
export type AdoptRequest = z.infer<typeof AdoptRequest>;

/**
 * Итог подтверждения:
 *
 * - `adopted`: перенос сделан этим запросом;
 * - `already`: этот же браузер уже подтвердил (двойное нажатие);
 * - `same_user`: ссылка уже ведёт к вошедшему (перенос сделан с другого
 *   устройства, встречное подтверждение). Набор сессии пополнен, как у
 *   погашения.
 */
export const AdoptResult = z.enum(["adopted", "already", "same_user"]);
export type AdoptResult = z.infer<typeof AdoptResult>;

/**
 * Ответ подтверждения: тело сессии, перечитанной после коммита, плюс то, что
 * нужно экрану результата (9.2). Токена нет: он не менялся.
 */
export const AdoptResponse = MeResponse.extend({
  result: AdoptResult,
  teacherDisplayName: DisplayName,
  /** Преподаватели, чьи занятия видны в этом браузере, по алфавиту. */
  scopeTeachers: z.array(DisplayName),
});
export type AdoptResponse = z.infer<typeof AdoptResponse>;

/** Тело `409 adopt_in_lesson`: заглушка это вошедший (С3) или ученик ссылки. */
export const AdoptInLesson = z.object({
  error: z.literal("adopt_in_lesson"),
  stubIsCurrent: z.boolean(),
});
export type AdoptInLesson = z.infer<typeof AdoptInLesson>;

/** Тело `404 student_moved`: ученик в адресе карточки перенесён. */
export const StudentMoved = z.object({
  error: z.literal("student_moved"),
  movedTo: UserId,
});
export type StudentMoved = z.infer<typeof StudentMoved>;
